import { format } from "date-fns";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { defaultButtonClasses } from "@/constants/ui";
import { useMember } from "@/contexts/memberContext";
import { useCanMoveBooking } from "@/hooks/useCanMoveBooking";
import { useRoomBookingsForDate } from "@/hooks/useRoomBookingsForDate";
import { isBeforeStartOfCurrentMonth, startOfCurrentMonth } from "@/utils/bookingDateBounds";
import { orgPartyMemberIds } from "@/utils/orgParty";
import { calculateAvailableSlots } from "@/utils/availableSlots";
import { canMakePastBookings } from "@/utils/userPermissions";
import { isPastDateBooking } from "@/utils/isPastDateBooking";
import { Check, ChevronLeft, ChevronRight, RotateCcw, Clock, Info, AlertTriangle } from "lucide-react";
import { useChangeBookingFlow } from "./ChangeBookingFlowProvider";
import DateTimeConfirmation from "./DateTimeConfirmation";
import type { CanMoveValidationResponse } from "@/types/CanMoveValidation";

type DateTimeProps = {
  onClose?: () => void;
};

const formatTime = (time: string) => `${time.slice(0, 2)}:${time.slice(2)}`;

/**
 * Pure helper: returns true when there is a navigable day before `selectedDate`
 * for the given user type.
 *
 * - Elevated users: floor is the first day of the current month (UTC midnight),
 *   matching startOfCurrentMonth / isBeforeStartOfCurrentMonth.
 * - Members: floor is today (UTC midnight).
 *
 * All comparisons use UTC normalisation so the result is timezone-invariant
 * (fixes a BST off-by-one where local setHours differed from UTC midnight by 1 h).
 *
 * @param selectedDate The currently displayed date.
 * @param today        The "now" reference (defaults to new Date()).
 * @param isElevated   Whether the user has elevated permissions.
 */
export function computeCanNavigateToPrev(
  selectedDate: Date,
  today: Date,
  isElevated: boolean,
): boolean {
  const candidate = new Date(selectedDate);
  candidate.setDate(candidate.getDate() - 1);
  candidate.setUTCHours(0, 0, 0, 0);

  const floor = isElevated
    ? startOfCurrentMonth(today)
    : (() => { const d = new Date(today); d.setUTCHours(0, 0, 0, 0); return d; })();

  return candidate >= floor;
}

const DateTime = ({ onClose }: DateTimeProps) => {
  const { booking, resetFlow } = useChangeBookingFlow();
  const { member } = useMember();
  const isElevated = canMakePastBookings(member);

  const originalDate = useMemo(() => (booking ? new Date(booking.date) : new Date()), [booking]);
  const [selectedDate, setSelectedDate] = useState<Date>(originalDate);
  const [selectedSlot, setSelectedSlot] = useState<{ startTime: string; endTime: string } | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [validationResult, setValidationResult] = useState<CanMoveValidationResponse | null>(null);

  const canMoveBooking = useCanMoveBooking();

  const locationSlug = booking?.location?.slug ?? "";
  const roomId = booking?.roomId ?? "";

  const { data: roomBookings, isLoading: isLoadingBookings } = useRoomBookingsForDate({
    locationSlug,
    roomId,
    date: selectedDate,
    enabled: !!booking,
  });

  // The org's own party — so an org's booking may be moved adjacent to another of the org's own
  // bookings (buffer waived), exactly as against the same member's. Undefined for non-org users.
  const sameOrgMemberIds = useMemo(() => orgPartyMemberIds(member), [member]);
  const availableSlots = useMemo(() => {
    if (!booking || !roomBookings) return [];
    return calculateAvailableSlots(roomBookings, booking, selectedDate, sameOrgMemberIds);
  }, [roomBookings, booking, selectedDate, sameOrgMemberIds]);

  // Single "now" reference used by both navigateDay and pastFloor/canNavigateToPrev.
  const today = new Date();

  const navigateDay = (offset: number) => {
    setSelectedDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + offset);

      // Elevated users: do not navigate before the start of the current month.
      // Members: do not navigate before today.
      if (isElevated) {
        if (isBeforeStartOfCurrentMonth(next, today)) return prev;
      } else {
        const floor = new Date(today);
        floor.setUTCHours(0, 0, 0, 0);
        const nextNorm = new Date(next);
        nextNorm.setUTCHours(0, 0, 0, 0);
        if (nextNorm < floor) return prev;
      }

      return next;
    });
    setSelectedSlot(null);
  };

  const canNavigateToPrev = computeCanNavigateToPrev(selectedDate, today, isElevated);

  const resetToOriginalDate = () => {
    setSelectedDate(originalDate);
    setSelectedSlot(null);
  };

  const isOriginalDate =
    selectedDate.getFullYear() === originalDate.getFullYear() &&
    selectedDate.getMonth() === originalDate.getMonth() &&
    selectedDate.getDate() === originalDate.getDate();

  const handleNext = async () => {
    if (!selectedSlot || !booking) return;

    const isRecurringBooking = !!(booking.recurrence || booking.recurrencePattern);
    const formattedDate = format(selectedDate, "yyyy-MM-dd");

    try {
      const result = await canMoveBooking.mutateAsync({
        bookingId: booking.id,
        newDate: formattedDate,
        newStartTime: selectedSlot.startTime,
        newEndTime: selectedSlot.endTime,
        checkRecurring: isRecurringBooking,
      });

      setValidationResult(result);
      setShowConfirmation(true);
    } catch (error) {
      console.error("Failed to validate booking move:", error);
    }
  };

  const handleCancel = () => {
    resetFlow();
    onClose?.();
  };

  if (!booking) {
    return (
      <div className="p-6">
        <p className="text-red-600">Error: No booking data available</p>
        <Button onClick={handleCancel} className="mt-4">
          Close
        </Button>
      </div>
    );
  }

  if (showConfirmation && validationResult && selectedSlot) {
    return (
      <DateTimeConfirmation
        onClose={onClose}
        validationResult={validationResult}
        newDate={selectedDate}
        newStartTime={selectedSlot.startTime}
        newEndTime={selectedSlot.endTime}
      />
    );
  }

  const duration = booking.endTime && booking.startTime
    ? `${formatTime(booking.startTime)} - ${formatTime(booking.endTime)}`
    : "";

  const dateLabel = selectedDate.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const isButtonDisabled = !selectedSlot || canMoveBooking.isPending;
  const hasAmenities = (booking.bookedAmenities?.length ?? 0) > 0;
  const isRecurring = !!(booking.recurrence || booking.recurrencePattern);

  const isPastMove = isPastDateBooking(isElevated, selectedDate, today);

  const slotListContent = (
    <>
      {/* Past-date warning — only shown to elevated users moving to a past date */}
      {isPastMove && (
        <div
          data-testid="past-date-warning"
          className="flex items-start gap-2 p-3 mb-4 bg-amber-50 border border-amber-300 rounded-lg"
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-900">
            You're moving this booking to a past date.
          </p>
        </div>
      )}

      {/* Date navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigateDay(-1)} disabled={!canNavigateToPrev}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{dateLabel}</span>
          {!isOriginalDate && (
            <Button variant="ghost" size="sm" onClick={resetToOriginalDate} title="Back to original date">
              <RotateCcw className="w-3 h-3" />
            </Button>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigateDay(1)}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Current booking info */}
      <div className="p-2 mb-4 bg-white rounded border border-gray-200">
        <p className="text-xs text-gray-500">Current booking</p>
        <p className="text-sm font-medium text-gray-800">
          {booking.room?.name ?? "Room"} &middot; {duration}
        </p>
      </div>

      {/* Slot list */}
      <ScrollArea className="flex-1" style={{ maxHeight: "calc(80vh - 380px)" }}>
        {isLoadingBookings ? (
          <div className="flex items-center justify-center py-12">
            <Spinner className="size-6" />
            <span className="ml-2 text-sm text-gray-500">Loading available times...</span>
          </div>
        ) : availableSlots.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Clock className="w-8 h-8 text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No available time slots on this date.</p>
            <p className="text-xs text-gray-400 mt-1">Try another day.</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {availableSlots.map((slot) => {
              const isSelected =
                selectedSlot?.startTime === slot.startTime && selectedSlot?.endTime === slot.endTime;
              return (
                <button
                  key={slot.startTime}
                  onClick={() => setSelectedSlot(slot)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md border text-sm transition-colors ${
                    isSelected
                      ? "border-indigo-500 bg-indigo-50 text-indigo-900 font-medium"
                      : "border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/50"
                  }`}
                >
                  <span>
                    {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                  </span>
                  {isSelected && <Check className="w-4 h-4 text-indigo-600" />}
                </button>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* Info notes */}
      {(hasAmenities || isRecurring) && (
        <div className="mt-3 space-y-1.5">
          {hasAmenities && (
            <div className="flex items-start gap-1.5 p-2 bg-blue-50 rounded border border-blue-200">
              <Info className="w-3.5 h-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-800">Your amenity bookings will move with this booking.</p>
            </div>
          )}
          {isRecurring && (
            <div className="flex items-start gap-1.5 p-2 bg-amber-50 rounded border border-amber-200">
              <Info className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">Recurring conflicts will be checked on the next step.</p>
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-[90vh] lg:min-h-[70vh] w-full flex flex-col bg-white rounded-xl my-4">
      {/* Header - Mobile only */}
      <div className="lg:hidden p-4 py-6 flex flex-col circle-pattern-background circle-pattern-indigo-50">
        <h2 className="text-2xl font-semibold mb-2">Select a New Time</h2>
        <p className="text-gray-600 text-xs">
          Choose from the available time slots below. You can navigate between days to find the perfect time.
        </p>
      </div>

      {/* Desktop grid layout */}
      <div className="hidden lg:grid lg:grid-cols-3 grow gap-1">
        {/* Left column - Information */}
        <div className="p-6 lg:rounded-lg flex flex-col circle-pattern-background circle-pattern-indigo-50">
          <h2 className="text-3xl font-semibold mb-4">Select a New Time</h2>
          <p className="mb-2 text-gray-600 text-sm">
            Choose from the available time slots. The booking will maintain its current duration and room.
            {isRecurring && " Changes will apply to all future occurrences."}
          </p>
          <div className="mt-4 space-y-2">
            <div className="p-2 bg-white rounded border border-gray-200">
              <p className="text-xs font-semibold text-gray-700">Current Booking:</p>
              <p className="text-sm text-gray-600">
                {new Date(booking.date).toLocaleDateString("en-GB", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}{" "}
                at {duration}
              </p>
            </div>
          </div>
        </div>

        {/* Right column - Slot list */}
        <div className="bg-gray-50 rounded-lg lg:rounded-r-xl lg:col-span-2 p-6 border-l border-gray-200 shadow-sm flex flex-col">
          {slotListContent}
          <div className="flex flex-col gap-2 mt-6 pt-4 border-t border-gray-200">
            <Button className={`w-full ${defaultButtonClasses}`} onClick={handleNext} disabled={isButtonDisabled}>
              {canMoveBooking.isPending ? (
                <>
                  <Spinner className="size-4 mr-2" />
                  Checking availability...
                </>
              ) : (
                "Next"
              )}
            </Button>
            <Button variant="ghost" className="w-full text-gray-600" onClick={handleCancel}>
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile content area */}
      <div className="lg:hidden flex-1 bg-gray-50 p-6 flex flex-col">
        {slotListContent}
        <div className="flex flex-col gap-2 mt-6 pt-4 border-t border-gray-200">
          <Button className={`w-full ${defaultButtonClasses}`} onClick={handleNext} disabled={isButtonDisabled}>
            {canMoveBooking.isPending ? (
              <>
                <Spinner className="size-4 mr-2" />
                Checking availability...
              </>
            ) : (
              "Next"
            )}
          </Button>
          <Button variant="ghost" className="w-full text-gray-600" onClick={handleCancel}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DateTime;
